import React from "react";

interface ToggleSwitchProps {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  showIcon?: boolean;
  containerClassName?: string;
  thumbClassName?: string;
  sizeVariant?: "xs" | "sm" | "md";
  showStatusText?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  id,
  checked,
  onChange,
  label = "Enable Geo‑Location",
  description = "Track location for enhanced functionality",
  showIcon = true,
  containerClassName,
  sizeVariant = "sm",
  showStatusText = false,
}) => {
  const inputId = id;

  const baseContainer =
    "flex items-center justify-between p-2 rounded-lg " +
    "bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/60";

  const isXs = sizeVariant === "xs";
  const isSm = sizeVariant === "sm";

  const trackSizeClass = isXs ? "h-5 w-9" : isSm ? "h-6 w-12" : "h-6 w-12";
  const thumbSizeClass = isXs ? "h-3 w-3" : "h-4 w-4";
  const translateOnClass = isXs
    ? "translate-x-5"
    : isSm
    ? "translate-x-7"
    : "translate-x-6";
  const translateOffClass = "translate-x-1";

  return (
    <div className={containerClassName ?? baseContainer}>
      <div className="flex items-center space-x-3">
        {showIcon && (
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-200">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
        )}
        <div>
          <label
            htmlFor={inputId}
            className="flex text-xs font-semibold text-slate-600 cursor-pointer hover:text-emerald-600 tracking-wide transition-colors duration-200 mr-2"
          >
            {label}
          </label>
        </div>
      </div>

      <div className="flex items-center">
        {/* accessible input kept for a11y; visually hide it */}
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
          aria-checked={checked}
          aria-label={label}
        />

        {/* Visual switch using the exact inline-flex + translate approach */}
        <button
          onClick={() => onChange(!checked)}
          type="button"
          aria-pressed={checked}
          className={`relative inline-flex items-center ${trackSizeClass} rounded-full transition-colors duration-300 ${
            checked ? "bg-emerald-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block ${thumbSizeClass} bg-white rounded-full transform transition-transform duration-300 ${
              checked ? translateOnClass : translateOffClass
            }`}
          />
        </button>
        {showStatusText && (
          <span
            className={`ml-2 text-xs font-medium ${
              checked ? "text-emerald-600" : "text-gray-500"
            }`}
          >
            {checked ? "ON" : "OFF"}
          </span>
        )}
      </div>
    </div>
  );
};

export default ToggleSwitch;
